import { EventModelConfig } from '@bryntum/schedulerpro';
import { Dictionary } from 'lodash';
import { Holiday as HolidayType } from 'api/holiday-calendar/types';
import {
  AllocationMedium,
  AllocationSourceTask,
  AllocationWorkType,
  OverAllocationApi,
} from 'api/resource-management/types';
import { TimeoffResponse } from 'api/timeoff/types';
import { Minutes } from '../user-capacity/types';
import { ViewPresetId } from './scheduler-config/view-presets/constants';
import { AllocatedMinutes } from './types';

export enum SchedulerEventType {
  UserAllocationSummary = 'user_allocation_summary',
  RequestedAllocation = 'requested_allocation',
  TeamAllocationSummary = 'team_allocation_summary',
  CompanyAllocationSummary = 'company_allocation_summary',
  ProjectAllocationSummary = 'project_allocation_summary',
  BudgetAllocationSummary = 'budget_allocation_summary',
  ResourceAllocationSummary = 'resource_allocation_summary',
  Allocation = 'allocation',
  ProjectTimeline = 'project_timeline',
  ProjectAsALeafTimeline = 'project_as_a_leaf_timeline',
  ProjectPhase = 'project_phase',
  DemandRole = 'demand_role',
  DemandCapacity = 'demand_capacity',
  TotalAllocation = 'total_allocation',
  UserTotalAllocation = 'user_total_allocation',
  UserTotalSoftAllocation = 'user_total_soft_allocation',
  UserTotalHardAllocation = 'user_total_hard_allocation',
  TeamTotalAllocation = 'team_total_allocation',
  TeamTotalSoftAllocation = 'team_total_soft_allocation',
  TeamTotalHardAllocation = 'team_total_hard_allocation',
  UserTimeOff = 'user_time_off',
  UserFte = 'user_fte',
  PartnerFte = 'partner_fte',
  TimeOffFte = 'timeoff_fte',
  HolidayFte = 'holiday_fte',
  CapacityAllocationDiffFte = 'capacity_allocation_diff_fte',
  ProjectsAllocationInRoleFte = 'projects_allocation_fte',
  Holiday = 'holiday',
  ProjectBudgetTimeline = 'project_budget_timeline',
  BudgetAsALeafTimeline = 'budget_as_a_leaf_timeline',
}
export type EventModelConfigWithoutMandatoryFields = Omit<
  EventModelConfig,
  'id' | 'resourceId' | 'startDate' | 'endDate'
>;

export interface OverAllocationForUi {
  startDate: Date;
  endDate: Date;
  overAllocatedMinutes: number;
}

export interface WeekendHighlightForUi {
  startDate: Date;
  endDate: Date;
  day: 'saturday' | 'sunday';
}

export interface BaseEvent
  extends Partial<EventModelConfigWithoutMandatoryFields> {
  id: string | number;
  resourceId: string;
  startDate: Date;
  endDate: Date;
}
export interface BaseAllocationEvent extends BaseEvent {
  type: SchedulerEventType.Allocation | SchedulerEventType.RequestedAllocation;
  minutes: number;
  totalMinutes: number;
  previewText?: string;
  projectPhase?: {
    id: number;
    name: string;
  };
  billable: boolean;
  overAllocations: OverAllocationApi[];
  overAllocationsForUI: OverAllocationForUi[];
  weekendHighlightForUI?: WeekendHighlightForUi[];
  includeSaturday?: boolean;
  includeSunday?: boolean;
  projectId: number;
  medium: AllocationMedium;
  sources?: AllocationSourceTask[];
  projectFinancialsBudgetId?: number;
  workId?: number;
  workType?: AllocationWorkType;
  // Stacking properties for overlapping allocations
  top?: number;
  rowIndex?: number;
  groupKey?: string;
}
export interface UserAllocationEvent extends BaseAllocationEvent {
  userId: number;
  roleId?: number;
  referenceTeamId?: number;
  rbacType?: string;
}
export interface TeamAllocationEvent extends BaseAllocationEvent {
  teamId: number;
  roleId: number;
  rbacType?: string;
}
export type AllocationEvent = UserAllocationEvent | TeamAllocationEvent;

export interface UserAllocationSummaryEvent extends BaseEvent {
  type: SchedulerEventType.UserAllocationSummary;
  allocatedMinutes: AllocatedMinutes;
  capacityMinutes: Minutes;
  forViewPreset: ViewPresetId;
  timeOff?: TimeoffResponse[];
}

export interface TeamAllocationSummaryEvent extends BaseEvent {
  type: SchedulerEventType.TeamAllocationSummary;
  allocatedMinutes: AllocatedMinutes;
  forViewPreset: ViewPresetId;
}
export interface CompanyAllocationSummaryEvent extends BaseEvent {
  type: SchedulerEventType.CompanyAllocationSummary;
  allocatedMinutes: AllocatedMinutes;
  forViewPreset: ViewPresetId;
}

export interface BudgetAllocationSummaryEvent extends BaseEvent {
  type: SchedulerEventType.BudgetAllocationSummary;
  allocatedMinutes: AllocatedMinutes;
  forViewPreset: ViewPresetId;
}

export interface ResourceAllocationSummaryEvent extends BaseEvent {
  type: SchedulerEventType.ResourceAllocationSummary;
  allocatedMinutes: AllocatedMinutes;
  forViewPreset: ViewPresetId;
  noOfWorkingDays?: number;
  includeSaturday?: boolean;
  includeSunday?: boolean;
  weekendHighlightForUI?: WeekendHighlightForUi[];
  overAllocationsForUI: OverAllocationForUi[];
}

export interface ProjectAllocationSummaryEvent extends BaseEvent {
  type: SchedulerEventType.ProjectAllocationSummary;
  capacityMinutes?: Minutes;
  allocatedMinutes: AllocatedMinutes;
  forViewPreset: ViewPresetId;
}

export type AllocationSummaryEvent =
  | UserAllocationSummaryEvent
  | TeamAllocationSummaryEvent
  | CompanyAllocationSummaryEvent
  | BudgetAllocationSummaryEvent
  | ProjectAllocationSummaryEvent
  | ResourceAllocationSummaryEvent;

export interface ProjectTimelineEvent extends BaseEvent {
  type: SchedulerEventType.ProjectTimeline;
  showInDetail?: boolean;
}

export interface ProjectBudgetTimelineEvent extends BaseEvent {
  type: SchedulerEventType.ProjectBudgetTimeline;
  showInDetail?: boolean;
}
export interface BudgetAsLeafTimelineEvent extends BaseEvent {
  type: SchedulerEventType.BudgetAsALeafTimeline;
}
export interface ProjectAsLeafTimelineEvent extends BaseEvent {
  type: SchedulerEventType.ProjectAsALeafTimeline;
}

export interface TimeOffEvent extends BaseEvent {
  type: SchedulerEventType.UserTimeOff;
  timeOff: TimeoffResponse;
  userId: number;
}

export interface HolidayEvent extends BaseEvent {
  type: SchedulerEventType.Holiday;
  holiday: HolidayType;
  userId: number;
}

export interface ProjectPhaseEvent extends BaseEvent {
  type: SchedulerEventType.ProjectPhase;
  phaseName: string;
  groupKey: string;
  rowIndex: number;
  activePhase: boolean;
  top: number;
  height: number;
  showInDetail?: boolean;
}
type BaseDemandSummaryEvent = BaseEvent & {
  roleId: number;
  capacityMinutes: Minutes; // holds both FTE and Minues value
  totalAllocationFte: number;
  totalAllocationMinutes: number;
  forViewPreset: ViewPresetId;
};
export interface DemandRoleEvent extends BaseDemandSummaryEvent {
  type: SchedulerEventType.DemandRole;
  isHoursView?: boolean;
}
export interface DemandCapacityEvent extends BaseEvent {
  type: SchedulerEventType.DemandCapacity;
  forViewPreset: ViewPresetId;
  roleId: number;
  capacityMinutes: Minutes;
  isHoursView?: boolean;
}
type BaseDemandCapacityDetailsEvent = BaseEvent & {
  roleId: number;
  forViewPreset: ViewPresetId;
};
export interface DemandCapacityTeamMemberFteEvent
  extends BaseDemandCapacityDetailsEvent {
  type: SchedulerEventType.UserFte;
  teamMemberCapacityFte: number;
  teamMemberCapacityMinutes: number;
  isHoursView?: boolean;
}
export interface DemandCapacityPartnerFteEvent
  extends BaseDemandCapacityDetailsEvent {
  type: SchedulerEventType.PartnerFte;
  partnerCapacityFte: number;
  partnerCapacityMinutes: number;
  isHoursView?: boolean;
}
export interface DemandCapacityTimeOffFteEvent
  extends BaseDemandCapacityDetailsEvent {
  type: SchedulerEventType.TimeOffFte;
  timeOffCapacityFte: number;
  timeOffCapacityMinutes: number;
  isHoursView?: boolean;
}
export interface DemandCapacityHolidayFteEvent
  extends BaseDemandCapacityDetailsEvent {
  type: SchedulerEventType.HolidayFte;
  holidayCapacityFte: number;
  holidayCapacityMinutes: number;
  isHoursView?: boolean;
}
export interface TotalAllocationEvent extends BaseDemandSummaryEvent {
  type: SchedulerEventType.TotalAllocation;
  isHoursView?: boolean;
}

export interface DemandFteDiffEvent extends BaseEvent {
  type: SchedulerEventType.CapacityAllocationDiffFte;
  forViewPreset: ViewPresetId;
  roleId: number;
  capacityAllocationDiffFte: number;
  capacityAllocationDiffMinutes: number;
  isHoursView?: boolean;
}

export interface ProjectsAllocationInRoleFteEvent extends BaseEvent {
  type: SchedulerEventType.ProjectsAllocationInRoleFte;
  forViewPreset: ViewPresetId;
  roleId: number;
  allocationInProjectFte: number;
  allocationInProjectMinutes: number;
  projectId: number;
  isHoursView?: boolean;
}

export type ResourceManagementEvents =
  | AllocationEvent
  | UserAllocationSummaryEvent
  | TeamAllocationSummaryEvent
  | CompanyAllocationSummaryEvent
  | ProjectTimelineEvent
  | ProjectBudgetTimelineEvent
  | ProjectAsLeafTimelineEvent
  | BudgetAsLeafTimelineEvent
  | ProjectPhaseEvent
  | TimeOffEvent
  | HolidayEvent
  | ProjectAllocationSummaryEvent
  | BudgetAllocationSummaryEvent
  | ResourceAllocationSummaryEvent;

export type DemandEvents =
  | DemandRoleEvent
  | DemandCapacityEvent
  | TotalAllocationEvent
  | DemandCapacityTeamMemberFteEvent
  | DemandCapacityPartnerFteEvent
  | DemandCapacityTimeOffFteEvent
  | DemandCapacityHolidayFteEvent
  | DemandFteDiffEvent
  | ProjectsAllocationInRoleFteEvent;

export interface RecourceSummaries {
  userSummaries: Dictionary<UserAllocationSummaryEvent[]>;
  teamSummaries: Dictionary<TeamAllocationSummaryEvent[]>;
  companySummaries: Dictionary<CompanyAllocationSummaryEvent[]>;
  userProjectSummaries: Dictionary<ProjectAllocationSummaryEvent[]>;
}

export enum AllocationFrom {
  People = 'People',
  Project = 'Projects',
}

export type ConvertAllocationsConfiguration = {
  isMultiLineBudgetsEnabled?: boolean;
};
